import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
  useState,
} from "react";

interface ConfirmParams {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
}

interface ContextType {
  confirm: (params?: ConfirmParams) => Promise<boolean>;
}

const ConfirmContext = createContext<ContextType | null>(null);

interface ConfirmProviderProps extends PropsWithChildren {}

const ConfirmProvider = (props: ConfirmProviderProps) => {
  const ref = useRef<(value: boolean) => void>();
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<ConfirmParams>();
  const confirm = (params?: ConfirmParams): Promise<boolean> => {
    setParams(params);
    setOpen(true);
    return new Promise((resolve) => {
      ref.current = resolve;
    });
  };
  const handleConfirm = () => {
    setOpen(false);
    ref.current?.(true);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {props.children}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {params?.title || "Are you sure?"}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {params?.message || "This action cannot be undone."}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="secondary">
                {params?.cancelText || "Cancel"}
              </Button>
            </AlertDialogCancel>
            <Button onClick={handleConfirm}>
              {params?.confirmText || "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};

export { ConfirmProvider, useConfirm };
